export type Product = 'boost' | 'saver'

/**
 * calculatePmt, calculateIpmt & calculatePpmt
 * adopted from https://gist.github.com/pies/4166888
 */
const calculatePmt = (
  rate: number,
  nper: number,
  pv: number,
  fv = 0,
  type = 0
) => {
  if (rate == 0) return -(pv + fv) / nper

  const pvif = Math.pow(1 + rate, nper)
  let pmt = (rate / (pvif - 1)) * -(pv * pvif + fv)

  if (type == 1) {
    pmt /= 1 + rate
  }

  return pmt
}

const calculateIpmt = (
  rate: number,
  per: number,
  nper: number,
  pv: number,
  fv?: number,
  type?: number
) => {
  const pmt = calculatePmt(rate, nper, pv, fv, type)
  const tmp = Math.pow(1 + rate, per - 1)
  return 0 - (pv * tmp * rate + pmt * (tmp - 1))
}

const calculatePpmt = (
  rate: number,
  per: number,
  nper: number,
  pv: number,
  fv?: number,
  type?: number
) => {
  if (per < 1 || per >= nper + 1) return 0
  const pmt = calculatePmt(rate, nper, pv, fv, type)
  const ipmt = calculateIpmt(rate, per, nper, pv, fv, type)
  return pmt - ipmt
}
/************/

const calculateYear = (months: number) => Math.ceil(months / 12)

const calculatePrincipal = (
  months: number,
  loanBalance: number,
  mortgageRate: number
) => {
  return calculatePpmt(mortgageRate / 12, months, 360, loanBalance)
}

const calculateCumulativePrincipal = (
  months: number,
  loanBalance: number,
  mortgageRate: number
) => {
  return Array.from(Array(months + 1).keys()).reduce((total, month) => {
    return total + calculatePrincipal(month, loanBalance, mortgageRate)
  })
}

const calculateInterest = (
  months: number,
  loanBalance: number,
  mortgageRate: number
) => {
  return calculateIpmt(mortgageRate / 12, months, 360, loanBalance)
}

// @TODO use projectedhomevalue at months instead of expectedclose
const calculatePmiTest1 = (
  loanBalance: number,
  months: number,
  expectedClose: number,
  mortgageRate: number
) => {
  return loanBalance +
    calculateCumulativePrincipal(months, loanBalance, mortgageRate) >
    expectedClose * 0.8
    ? 1
    : 0
}

const calculatePmiTest2 = (
  loanBalance: number,
  months: number,
  projectedHomeValue: number,
  mortgageRate: number
) => {
  return loanBalance +
    calculateCumulativePrincipal(months, loanBalance, mortgageRate) <
    0.75 * projectedHomeValue && months > 24
    ? 0
    : 1
}

export const calculateExpectedClose = (
  salePercentOverListed: number,
  homeListPrice: number
) => {
  return (1 + salePercentOverListed / 100) * homeListPrice
}

export const calculateEstAppreciation = (
  projectedHomeValue: number,
  expectedClose: number
) => {
  return projectedHomeValue - expectedClose
}

export const calculateProductEstAppreciation = (
  projectedHomeValue: number,
  expectedClose: number,
  upsidePercent: number,
  downsidePercent: number
) => {
  const change = calculateEstAppreciation(projectedHomeValue, expectedClose)
  return change > 0 ? change * upsidePercent : change * downsidePercent
}

export const calculateMonthlyMortgage = (
  loanBalance: number,
  mortgageRate: number
) => {
  return -1 * calculatePmt(mortgageRate / 12, 360, loanBalance)
}

export type CalculatorAssumptions = {
  hpa: number
  saverUpsideShare: number
  saverDownsideShare: number
  boostUpsideShare: number
  boostDownsideShare: number
  propertyTaxRate?: number
  atlantaPropertyTaxRate: number
  taxGrowthMultiplier: number
  propertyAnnualTaxAmount?: number
  inflationRate: number
  insuranceRate: number
  pmiRate: number
  buyMaintenanceRate: number
  acreMaintenanceRate: number
  saleTransactionCostRate: number
  earlyOutFeePercent: number
  cashOutFeePercent: number
  buyTransactionCostRate: number
  /** @deprecated 2022-12-01 */
  acreFeeIncrease: number
  /** @deprecated 2022-12-01 */
  hpaSt: number
  /** @deprecated 2022-12-01 */
  hpaLt: number
  /** @deprecated 2022-12-01 */
  stLength: number
  /** @deprecated 2022-12-01 */
  buyTransactionCostFixedAmount: number
  investmentReturnPercentage: number
  boostDiscountPercentage: number
  saverDiscountPercentage: number
}

export const calculatorFns = (assumptions: CalculatorAssumptions) => {
  const {
    hpa: HPA,
    propertyTaxRate: PROPERTY_TAX_RATE,
    atlantaPropertyTaxRate: ATLANTA_PROPERTY_TAX_RATE,
    propertyAnnualTaxAmount: PROPERTY_TAX_ANNUAL_AMOUNT,
    taxGrowthMultiplier: TAX_GROWTH_MULTIPLIER,
    inflationRate: INFLATION_RATE,
    insuranceRate: INSURANCE_RATE,
    pmiRate: PMI_RATE,
    buyMaintenanceRate: BUY_MAINTENANCE_RATE,
    acreMaintenanceRate: ACRE_MAINTENANCE_RATE,
    saleTransactionCostRate: SALE_TRANSACTION_COST_RATE,
    earlyOutFeePercent: EARLY_OUT_FEE_PERCENT,
    cashOutFeePercent: CASH_OUT_FEE_PERCENT,
    buyTransactionCostRate: BUY_TRANSACTION_COST_RATE,
    boostUpsideShare: BOOST_UPSIDE_SHARE,
    boostDownsideShare: BOOST_DOWNSIDE_SHARE,
    saverUpsideShare: SAVER_UPSIDE_SHARE,
    saverDownsideShare: SAVER_DOWNSIDE_SHARE,
    investmentReturnPercentage: INVESTMENT_RETURN_PERCENTAGE,
    boostDiscountPercentage: BOOST_DISCOUNT_PERCENTAGE,
    saverDiscountPercentage: SAVER_DISCOUNT_PERCENTAGE,
  } = assumptions

  const calculateSaverEstAppreciation = (
    projectedHomeValue: number,
    expectedClose: number
  ) => {
    return calculateProductEstAppreciation(
      projectedHomeValue,
      expectedClose,
      SAVER_UPSIDE_SHARE,
      SAVER_DOWNSIDE_SHARE
    )
  }

  const calculateBoostEstAppreciation = (
    projectedHomeValue: number,
    expectedClose: number
  ) => {
    return calculateProductEstAppreciation(
      projectedHomeValue,
      expectedClose,
      BOOST_UPSIDE_SHARE,
      BOOST_DOWNSIDE_SHARE
    )
  }

  const calculatePropertyTaxes = (expectedClose: number, market?: string) => {
    const propertyAnnualTaxAmount = PROPERTY_TAX_ANNUAL_AMOUNT
      ? PROPERTY_TAX_ANNUAL_AMOUNT
      : expectedClose *
        (market === 'GA/atlanta'
          ? ATLANTA_PROPERTY_TAX_RATE
          : PROPERTY_TAX_RATE ?? 0)

    // This captures the tax logic from Finance, but shouldn't
    // currently be used in the app
    // if (year > termLength) {
    //   return (
    //     ((-1 * propertyAnnualTaxAmount) / 12) *
    //     Math.pow(1 + INFLATION_RATE, year - 1)
    //   )
    // }

    return (-1 * propertyAnnualTaxAmount * TAX_GROWTH_MULTIPLIER) / 12
  }

  const calculateInsurance = (expectedClose: number, year: number) => {
    if (year <= 3) {
      return (-1 * expectedClose * INSURANCE_RATE) / 12
    }

    /** @TODO revisit > 36 months logic */

    return (
      ((-1 * expectedClose * INSURANCE_RATE) / 12) *
      Math.pow(1 + INFLATION_RATE, year - 1)
    )
  }

  const _calculatePmi = (
    loanBalance: number,
    pmiTest1: number,
    pmiTest2: number,
    pmiCost: number
  ) => {
    return (
      ((-1 * loanBalance * PMI_RATE) / 12) * Math.min(pmiTest1, pmiTest2) +
      pmiCost
    )
  }

  const _calculateBuyMaintenance = (projectedHomeValue: number) => {
    return (-1 * projectedHomeValue * BUY_MAINTENANCE_RATE) / 12
  }

  const _calculateAcreMaintenance = (months: number, expectedClose: number) => {
    return (
      (-1 *
        calculateProjectedHomeValue(months, expectedClose) *
        ACRE_MAINTENANCE_RATE) /
      12
    )
  }

  const calculateMortgageTotalValue = (
    loanBalance: number,
    months: number,
    expectedClose: number,
    valueShare: number,
    mortgageRate: number
  ) => {
    const principal = calculateCumulativePrincipal(
      months,
      loanBalance,
      mortgageRate
    )

    const projectedHomeValue = calculateProjectedHomeValue(
      months,
      expectedClose
    )

    return (
      projectedHomeValue * (1 - SALE_TRANSACTION_COST_RATE) -
      principal -
      expectedClose * BUY_TRANSACTION_COST_RATE -
      expectedClose +
      valueShare
    )
  }

  const _calculateBuyTotalOutflow = (
    loanBalance: number,
    months: number,
    expectedClose: number,
    expectedClosingCosts: number,
    mortgageRate: number,
    market?: string
  ) => {
    if (months === 0) {
      return -1 * (expectedClose - loanBalance + expectedClosingCosts)
    }
    const year = calculateYear(months)
    const principal = calculatePrincipal(months, loanBalance, mortgageRate)
    const interest = calculateInterest(months, loanBalance, mortgageRate)
    const projectedHomeValue = calculateProjectedHomeValue(
      months,
      expectedClose
    )
    const pmiTest1 = calculatePmiTest1(
      loanBalance,
      months,
      expectedClose,
      mortgageRate
    )
    const pmiTest2 = calculatePmiTest2(
      loanBalance,
      months,
      projectedHomeValue,
      mortgageRate
    )
    const pmiCost = _calculatePmiCost(
      pmiTest1,
      pmiTest2,
      loanBalance,
      months,
      expectedClose,
      mortgageRate
    )
    const taxes = calculatePropertyTaxes(expectedClose, market)
    const insurance = calculateInsurance(expectedClose, year)
    const pmi = _calculatePmi(loanBalance, pmiTest1, pmiTest2, pmiCost)
    const buyMaintenance = _calculateBuyMaintenance(projectedHomeValue)
    return principal + interest + taxes + insurance + pmi + buyMaintenance // code assumes no itemized deduction here
  }

  const _calculatePmiCost = (
    pmiTest1: number,
    pmiTest2: number,
    loanBalance: number,
    months: number,
    expectedClose: number,
    mortgageRate: number
  ) => {
    return pmiTest1 === 1 &&
      pmiTest2 === 0 &&
      calculatePmiTest2(
        loanBalance,
        months - 1,
        calculateProjectedHomeValue(months - 1, expectedClose),
        mortgageRate
      ) === 1
      ? -600
      : 0
  }

  const calculateProjectedHomeValue = (
    months: number,
    expectedClose: number
  ) => {
    return Array.from(Array(months).keys()).reduce((total) => {
      return total * Math.pow(1 + HPA, 1 / 12)
    }, expectedClose)
  }

  const calculateMortgagePmi = (
    loanBalance: number,
    expectedClose: number,
    valueShare: number
  ) => {
    return valueShare / expectedClose < 0.2 ? (loanBalance * PMI_RATE) / 12 : 0
  }

  // @TODO early out changing to be a diff percent at end of year 1 and end of year 2 and then a third for beyond
  // based on projected home value AT THE TIME not keyed of year 1
  const calculateEarlyOutFee = (expectedClose: number) => {
    const projectedHomeValueAt1Year = calculateProjectedHomeValue(
      12,
      expectedClose
    )
    return projectedHomeValueAt1Year * EARLY_OUT_FEE_PERCENT
  }

  const calculateCashOutFee = (projectedHomeValue: number) => {
    return projectedHomeValue * CASH_OUT_FEE_PERCENT
  }

  const calculateExpectedClosingCosts = (expectedClose: number) => {
    return expectedClose * BUY_TRANSACTION_COST_RATE
  }
  const calculateSaverMonthlyPayment = (monthlyTotalMortgage: number) => {
    return monthlyTotalMortgage * (1 - SAVER_DISCOUNT_PERCENTAGE)
  }

  const calculateBoostMonthlyPayment = (monthlyTotalMortgage: number) => {
    return monthlyTotalMortgage * (1 - BOOST_DISCOUNT_PERCENTAGE)
  }

  const _calculateProductOutflow = (
    valueShare: number,
    months: number,
    expectedClose: number,
    productMonthlyPayment: number
  ) => {
    if (months === 0) {
      return -1 * valueShare
    }

    const maintenance = _calculateAcreMaintenance(months, expectedClose)

    return -1 * productMonthlyPayment + maintenance
  }

  const _calculateProductExcessCashflow = (
    loanBalance: number,
    valueShare: number,
    months: number,
    expectedClose: number,
    productMonthlyPayment: number,
    expectedClosingCosts: number,
    mortgageRate: number,
    market?: string
  ) => {
    const outflow = _calculateProductOutflow(
      valueShare,
      months,
      expectedClose,
      productMonthlyPayment
    )

    const buyOutflow = _calculateBuyTotalOutflow(
      loanBalance,
      months,
      expectedClose,
      expectedClosingCosts,
      mortgageRate,
      market
    )
    return outflow - buyOutflow
  }

  const _calculateProductSavings = (
    loanBalance: number,
    valueShare: number,
    months: number,
    expectedClose: number,
    productMonthlyPayment: number,
    expectedClosingCosts: number,
    mortgageRate: number,
    market?: string
  ) => {
    return Array.from(Array(months).keys()).reduce((total, month) => {
      return (
        total * Math.pow(1 + INVESTMENT_RETURN_PERCENTAGE, 1 / 12) +
        _calculateProductExcessCashflow(
          loanBalance,
          valueShare,
          month + 1,
          expectedClose,
          productMonthlyPayment,
          expectedClosingCosts,
          mortgageRate,
          market
        )
      )
    }, _calculateProductExcessCashflow(loanBalance, valueShare, 0, expectedClose, productMonthlyPayment, expectedClosingCosts, mortgageRate, market))
  }

  const _calculateProductEquity = (
    product: Product,
    months: number,
    expectedClose: number,
    valueShare: number
  ) => {
    const projectedHomeValue = calculateProjectedHomeValue(
      months,
      expectedClose
    )
    const appreciation =
      product === 'saver'
        ? calculateSaverEstAppreciation(projectedHomeValue, expectedClose)
        : calculateBoostEstAppreciation(projectedHomeValue, expectedClose)
    return appreciation + valueShare
  }

  const calculateProductTotalValue = (
    product: Product,
    loanBalance: number,
    valueShare: number,
    months: number,
    expectedClose: number,
    monthlyTotalMortgage: number,
    productMonthlyPayment: number,
    expectedClosingCosts: number,
    mortgageRate: number,
    mortgageRateAcre?: number,
    market?: string
  ) => {
    const investment = _calculateProductSavings(
      loanBalance,
      valueShare,
      months,
      expectedClose,
      productMonthlyPayment,
      expectedClosingCosts,
      mortgageRate,
      market
    )

    const equity = _calculateProductEquity(
      product,
      months,
      expectedClose,
      valueShare
    )

    const initialExcess = _calculateProductExcessCashflow(
      loanBalance,
      valueShare,
      0,
      expectedClose,
      monthlyTotalMortgage,
      expectedClosingCosts,
      mortgageRateAcre || mortgageRate
    )

    return investment + equity - initialExcess
  }

  const calculateProductTermSavings = (
    product: Product,
    months: number,
    expectedClose: number,
    valueShare: number,
    productTotalValue: number
  ) => {
    const equity = _calculateProductEquity(
      product,
      months,
      expectedClose,
      valueShare
    )
    return productTotalValue - equity
  }

  return {
    calculateProjectedHomeValue,
    calculateExpectedClosingCosts,
    calculateCumulativePrincipal,
    calculatePropertyTaxes,
    calculateInsurance,
    calculateMortgagePmi,
    calculateMortgageTotalValue,
    calculateSaverEstAppreciation,
    calculateBoostEstAppreciation,
    calculateSaverMonthlyPayment,
    calculateBoostMonthlyPayment,
    calculateProductTotalValue,
    calculateProductTermSavings,
    calculateEarlyOutFee,
    calculateCashOutFee,
  }
}
